<template>
    <div class="AvatarEditor">
        <div class="AvatarEditor__wrapper">
            <div v-show="step === 1">
                <div class="AvatarEditor__droparea" @dragleave.prevent @dragover.prevent @dragenter.prevent
                     @click="handleClick"
                     @drop.prevent="handleChange">
                    <i class="fas fa-upload AvatarEditor__droparea-icon"></i>
                    <span class="AvatarEditor__droparea-hint">Cliquez ou glissez votre avatar ici.</span>
                    <span class="AvatarEditor__droparea-not-supported" v-show="!isSupported">Votre navigateur n'est pas supporté. Utilisez IE10 + ou un autre navigateur s'il vous plaît.</span>
                    <input type="file" v-show="false" v-if="step === 1" @change.prevent="handleChange" ref="fileinput">
                </div>
                <div class="AvatarEditor__error" v-show="hasError">
                    <i class="fas fa-exclamation-circle m-r-5"></i> {{errorMsg}}
                </div>
                <div class="text-right m-t-20">
                    <button type="button" @click="close" class="Btn Btn--stroked">Annuler</button>
                </div>
            </div>
            <div v-if="step === 2">
                <div class="AvatarEditor__cropper">
                    <div class="AvatarEditor__cropper-container">
                        <img v-if="sourceImgUrl" :src="sourceImgUrl" :style="sourceImgStyle"
                             class="AvatarEditor__cropper-img"
                             ref="img" alt=""/>
                        <div
                            class="AvatarEditor__cropper-mask"
                            :draggable="false"
                            @drag.prevent
                            @dragstart.prevent
                            @dragend.prevent
                            @dragleave.prevent
                            @dragover.prevent
                            @dragenter.prevent
                            @drop.prevent
                            @touchstart.prevent="imgStartMove"
                            @touchmove.prevent="imgMove"
                            @touchend="createImg"
                            @touchcancel="createImg"
                            @mousedown.prevent="imgStartMove"
                            @mousemove.prevent="imgMove"
                            @mouseup="createImg"
                            @mouseout="createImg"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 300" width="300px" height="300px">
                                <defs>
                                    <mask id="mask" x="0" y="0" width="300" height="300">
                                        <rect x="0" y="0" width="300" height="300" fill="#fff"></rect>
                                        <circle cx="150" cy="150" r="150"></circle>
                                    </mask>
                                </defs>
                                <rect x="0" y="0" width="300" height="300" fill-opacity="0.7" fill="black"
                                      mask="url(#mask)"></rect>
                            </svg>
                        </div>
                    </div>
                    <div class="AvatarEditor__cropper-range">
                        <button type="submit" @mousedown="startZoomSub" @mouseout="endZoomSub" @mouseup="endZoomSub">
                            <i class="fas fa-minus"></i>
                        </button>
                        <input type="range" :value="scale.range" step="1"
                               min="0" max="100"
                               @input.prevent="zoomChange"/>
                        <button type="submit" @mousedown="startZoomAdd" @mouseout="endZoomAdd" @mouseup="endZoomAdd">
                            <i class="fas fa-plus"></i>
                        </button>
                    </div>
                </div>
                <div class="text-right m-t-20">
                    <button type="button" @click="step = 1" class="Btn Btn--stroked m-r-15">Retour</button>
                    <button type="button" @click="upload" class="Btn Btn--primary">Sauvegarder</button>
                </div>
            </div>
            <div v-if="step === 3">
                <div class="AvatarEditor__upload">
                    <div v-show="loading === 1">
                        <span class="AvatarEditor__loading"><i class="fas fa-cloud m-r-5"></i> Téléchargement...</span>
                        <div class="AvatarEditor__progress">
                            <span class="AvatarEditor__progress-bar" :style="progressStyle"></span>
                        </div>
                    </div>
                    <div class="AvatarEditor__error" v-show="hasError">
                        <i class="fas fa-exclamation-circle m-r-5"></i> {{errorMsg}}
                    </div>
                    <div class="AvatarEditor__success" v-show="loading === 2">
                        <i class="fas fa-check-double m-r-5"></i> Téléchargement réussit.
                    </div>
                </div>
                <div class="text-right m-t-20">
                    <button type="button" @click="step = 2" class="Btn Btn--stroked m-r-15">Retour</button>
                    <button type="button" @click="close" :disabled="loading === 1" class="Btn Btn--primary">Fermer
                    </button>
                </div>
            </div>
            <canvas v-show="false" :width="width" :height="height" ref="canvas"></canvas>
        </div>
    </div>
</template>

<script>
    import Api from "../../../services/Api";
    import {actions} from "../../../store";
    import {dataToBlob} from "../../../utils";

    export default {
        data() {
            return {
                isSupported: typeof FormData === 'function',
                isSupportTouch: document.hasOwnProperty("ontouchstart"),

                width: 300,
                height: 300,
                ratio: 1,
                maxSize: 10240,

                step: 1,
                loading: 0,
                progress: 0,
                hasError: false,
                errorMsg: '',

                sourceImg: null,
                sourceImgUrl: null,
                createImgUrl: '',

                sourceImgMouseDown: {
                    on: false,
                    mX: 0,
                    mY: 0,
                    x: 0,
                    y: 0
                },
                sourceImgContainer: {
                    width: 300,
                    height: 300
                },
                scale: {
                    zoomAddOn: false,
                    zoomSubOn: false,
                    range: 1,
                    x: 0,
                    y: 0,
                    width: 0,
                    height: 0,
                    maxWidth: 0,
                    maxHeight: 0,
                    minWidth: 0,
                    minHeight: 0,
                    naturalWidth: 0,
                    naturalHeight: 0
                }
            }
        },
        computed: {
            progressStyle() {
                return {
                    width: this.progress + '%'
                };
            },
            sourceImgStyle() {
                let {scale, sourceImgMasking} = this,
                    top = scale.y + sourceImgMasking.y + 'px',
                    left = scale.x + sourceImgMasking.x + 'px';

                return {
                    top,
                    left,
                    width: scale.width + 'px',
                    height: scale.height + 'px'
                };
            },
            sourceImgMasking() {
                let {width, height, ratio, sourceImgContainer} = this,
                    sourceImgContainerRatio = sourceImgContainer.width / sourceImgContainer.height,
                    x = 0,
                    y = 0,
                    containerWidth = sourceImgContainer.width,
                    containerHeight = sourceImgContainer.height,
                    scale = 1;

                if (ratio < sourceImgContainerRatio) {
                    scale = sourceImgContainer.height / height;
                    containerWidth = sourceImgContainer.height * ratio;
                    x = (sourceImgContainer.width - containerWidth) / 2;
                }

                if (ratio > sourceImgContainerRatio) {
                    scale = sourceImgContainer.width / width;
                    containerHeight = sourceImgContainer.width / ratio;
                    y = (sourceImgContainer.height - containerHeight) / 2;
                }

                return {
                    scale,
                    x,
                    y,
                    width: containerWidth,
                    height: containerHeight
                };
            },
        },
        methods: {
            reset() {
                this.loading = 0;
                this.hasError = false;
                this.errorMsg = '';
                this.progress = 0;
            },
            close() {
                if (this.loading === 1) {
                    return;
                }

                this.$emit('close');

                setTimeout(() => {
                    this.step = 1;
                    this.reset();
                }, 500)
            },
            handleClick(e) {
                if (e.target !== this.$refs.fileinput) {
                    if (document.activeElement !== this.$refs) {
                        this.$refs.fileinput.click();
                    }
                }
            },
            handleChange(e) {
                this.reset();

                let files = e.target.files || e.dataTransfer.files;
                if (this.checkFile(files[0])) {
                    this.setSourceImg(files[0]);
                }
            },
            checkFile(file) {
                if (file.type.indexOf('image') === -1) {
                    this.hasError = true;
                    this.errorMsg = 'Image uniquement';
                    return false;
                }

                if (file.size / 1024 > this.maxSize) {
                    this.hasError = true;
                    this.errorMsg = 'L\'image sélectionnée dépasse le poids maximum: ' + this.maxSize + 'kb';
                    return false;
                }

                return true;
            },
            setSourceImg(file) {
                let reader = new FileReader();
                reader.onload = () => {
                    this.sourceImgUrl = reader.result;
                    this.startCrop();
                };
                reader.readAsDataURL(file);
            },
            startCrop() {
                let {
                        width,
                        height,
                        ratio,
                        scale,
                        sourceImgUrl,
                        sourceImgMasking,
                    } = this,
                    masking = sourceImgMasking,
                    img = new Image();

                img.src = sourceImgUrl;
                img.onload = () => {
                    let nWidth = img.naturalWidth,
                        nHeight = img.naturalHeight,
                        nRatio = nWidth / nHeight,
                        w = masking.width,
                        h = masking.height,
                        x = 0,
                        y = 0;

                    if (nWidth < width || nHeight < height) {
                        this.hasError = true;
                        this.errorMsg = 'L\'image sélectionnée est trop petite. Dimensions minimum attendues: ' + width + '*' + height + 'px';
                        return false;
                    }

                    if (ratio > nRatio) {
                        h = w / nRatio;
                        y = (masking.height - h) / 2;
                    }

                    if (ratio < nRatio) {
                        w = h * nRatio;
                        x = (masking.width - w) / 2;
                    }

                    scale.range = 0;
                    scale.x = x;
                    scale.y = y;
                    scale.width = w;
                    scale.height = h;
                    scale.minWidth = w;
                    scale.minHeight = h;
                    scale.maxWidth = nWidth * masking.scale;
                    scale.maxHeight = nHeight * masking.scale;
                    scale.naturalWidth = nWidth;
                    scale.naturalHeight = nHeight;

                    this.sourceImg = img;
                    this.createImg();
                    this.step = 2;
                };
            },
            imgStartMove(e) {
                if (this.isSupportTouch && !e.targetTouches) {
                    return false;
                }

                let targetTouch = e.targetTouches ? e.targetTouches[0] : e;

                this.sourceImgMouseDown = {
                    ...this.sourceImgMouseDown,
                    mX: targetTouch.screenX,
                    mY: targetTouch.screenY,
                    x: this.scale.x,
                    y: this.scale.y,
                    on: true
                };
            },
            imgMove(e) {
                if (this.isSupportTouch && !e.targetTouches) {
                    return false;
                }

                let {
                        scale,
                        sourceImgMasking,
                        sourceImgMouseDown: {
                            on,
                            mX,
                            mY,
                            x,
                            y
                        }
                    } = this,
                    targetTouch = e.targetTouches ? e.targetTouches[0] : e,
                    nX = targetTouch.screenX,
                    nY = targetTouch.screenY,
                    dX = nX - mX,
                    dY = nY - mY,
                    rX = x + dX,
                    rY = y + dY;

                if (!on) {
                    return;
                }

                if (rX > 0) {
                    rX = 0;
                }

                if (rY > 0) {
                    rY = 0;
                }

                if (rX < sourceImgMasking.width - scale.width) {
                    rX = sourceImgMasking.width - scale.width;
                }

                if (rY < sourceImgMasking.height - scale.height) {
                    rY = sourceImgMasking.height - scale.height;
                }

                scale.x = rX;
                scale.y = rY;
            },

            startZoomAdd() {
                let {scale} = this;
                scale.zoomAddOn = true;

                const zoom = () => {
                    if (scale.zoomAddOn) {
                        let range = scale.range >= 100 ? 100 : ++scale.range;
                        this.zoomImg(range);
                        setTimeout(() => {
                            zoom();
                        }, 60);
                    }
                };
                zoom();
            },
            endZoomAdd() {
                this.scale.zoomAddOn = false;
            },
            startZoomSub() {
                let {scale} = this;
                scale.zoomSubOn = true;

                const zoom = () => {
                    if (scale.zoomSubOn) {
                        let range = scale.range <= 0 ? 0 : --scale.range;
                        this.zoomImg(range);
                        setTimeout(() => {
                            zoom();
                        }, 60);
                    }
                };
                zoom();
            },
            endZoomSub() {
                this.scale.zoomSubOn = false;
            },
            zoomChange(e) {
                this.zoomImg(e.target.value);
            },
            zoomImg(newRange) {
                let {sourceImgMasking, scale} = this,
                    {
                        maxWidth,
                        maxHeight,
                        minWidth,
                        minHeight,
                        width,
                        height,
                        x,
                        y
                    } = scale,
                    masking = sourceImgMasking,
                    sWidth = masking.width,
                    sHeight = masking.height,
                    nWidth = minWidth + (maxWidth - minWidth) * newRange / 100,
                    nHeight = minHeight + (maxHeight - minHeight) * newRange / 100,
                    nX = sWidth / 2 - (nWidth / width) * (sWidth / 2 - x),
                    nY = sHeight / 2 - (nHeight / height) * (sHeight / 2 - y);

                if (nX > 0) {
                    nX = 0;
                }

                if (nY > 0) {
                    nY = 0;
                }

                if (nX < sWidth - nWidth) {
                    nX = sWidth - nWidth;
                }

                if (nY < sHeight - nHeight) {
                    nY = sHeight - nHeight;
                }

                scale.x = nX;
                scale.y = nY;
                scale.width = nWidth;
                scale.height = nHeight;
                scale.range = newRange;

                setTimeout(() => {
                    if (scale.range === newRange) {
                        this.createImg();
                    }
                }, 300);
            },

            createImg(e) {
                let {
                        sourceImg,
                        scale: {
                            x,
                            y,
                            width,
                            height,
                        },
                        sourceImgMasking: {
                            scale
                        }
                    } = this,
                    canvas = this.$refs.canvas,
                    ctx = canvas.getContext('2d');

                if (e) {
                    this.sourceImgMouseDown.on = false;
                }

                canvas.width = this.width;
                canvas.height = this.height;

                ctx.clearRect(0, 0, this.width, this.height);
                ctx.fillStyle = '#fff';
                ctx.fillRect(0, 0, this.width, this.height);

                ctx.drawImage(sourceImg, x / scale, y / scale, width / scale, height / scale);
                this.createImgUrl = canvas.toDataURL('image/jpeg');
            },
            async upload() {
                try {
                    let formData = new FormData();

                    formData.append('file', dataToBlob(this.createImgUrl, 'image/jpeg'), 'avatar.jpg');
                    formData.append('_method', 'PUT');

                    this.reset();
                    this.loading = 1;
                    this.step = 3;

                    const {data} = await Api.post('/account/profile/avatar', formData, {
                        onUploadProgress: (event) => {
                            this.progress = Math.round((event.loaded * 100) / event.total);
                        }
                    });

                    await actions.fetchUser();

                    this.loading = 2;
                    this.$emit('change');
                } catch (err) {
                    this.loading = 3;
                    this.hasError = true;
                    this.errorMsg = 'Téléchargement echoué';
                }
            }
        }
    }

</script>
